import type { PayoutRescueTranslations } from "../interfaces/payoutRescue";

export const payoutRescueTranslations: PayoutRescueTranslations = {
  // Page
  page_title: "Payout rescue",
  page_subtitle: "Payouts whose automatic dispatch failed: retry the send or return the funds",
  refresh_button: "Refresh",
  refresh_aria: "Refresh the queue",
  error_load: "We couldn't load the rescue queue.",
  error_retry: "Retry",

  // KPI strip
  kpi_pending_label: "To rescue",
  kpi_pending_caption: "Action required",
  kpi_processing_label: "In progress",
  kpi_processing_caption: "At the bank",
  kpi_paid_today_label: "Paid today",
  kpi_paid_today_caption: "Settled",
  kpi_retained_label: "Held amount",

  // Tabs
  tab_pending: "To rescue",
  tab_processing: "In progress",
  tab_history: "History",

  // Filters
  filter_search_placeholder: "Search by ID, organization or concept",
  filter_org_label: "Organization",
  filter_all_orgs: "All organizations",
  filter_corridor_label: "Corridor",
  filter_all_corridors: "All corridors",
  filter_status_label: "Status",
  filter_all_statuses: "All statuses",
  filter_clear: "Clear filters",
  export_button: "Export",

  // Status labels
  status_created: "To rescue",
  status_processing: "Processing",
  status_paid: "Paid",
  status_failed: "Failed",
  status_cancelled: "Cancelled",
  status_reversed: "Reversed",
  status_long_created: "Needs rescue",
  status_long_processing: "Processing at the bank",
  status_long_paid: "Paid",
  status_long_failed: "Rejected by the bank",
  status_long_cancelled: "Cancelled",
  status_long_reversed: "Reversed",

  // Table
  table_th_age: "Age",
  table_th_org: "Organization",
  table_th_corridor: "Corridor",
  table_th_beneficiary: "Beneficiary",
  table_th_concept: "Concept",
  table_th_net: "Net",
  table_th_status: "Status",
  action_review: "Review",
  action_view: "View",
  empty_pending_title: "All clear",
  empty_pending_description: "No payout needs rescuing right now.",
  empty_processing_title: "Nothing in progress",
  empty_processing_description: "No payouts are waiting for bank confirmation.",
  empty_history_title: "No history yet",
  empty_history_description: "There are no finished payouts yet.",
  empty_filtered_description: "No payouts match the selected filters.",
  queue_truncated: "Showing the first {{shown}} of {{total}} — narrow the list with the filters.",
  pagination_summary: "Showing {{from}}–{{to}} of {{total}}",
  footer_order_pending: "Order: oldest first",

  // Drawer
  drawer_title: "Payout {{id}}",
  drawer_rescue_alert:
    "The automatic dispatch did not complete. Retry the send once the configuration is fixed, or cancel to return the funds.",
  drawer_org: "Organization",
  drawer_corridor: "Corridor",
  drawer_beneficiary: "Beneficiary",
  drawer_destination_unavailable: "Destination details not available in this view",
  drawer_concept: "Concept",
  drawer_reference: "Reference",
  drawer_created: "Created",
  drawer_failure_reason: "Reason",
  copy_aria: "Copy",
  copy_success: "Copied to clipboard",
  destination_label_clabe: "CLABE",
  destination_label_pix: "PIX key ({{type}})",
  destination_label_cbu_cvu: "{{type}} account",

  // Breakdown
  breakdown_title: "Breakdown",
  breakdown_gross: "Gross (debited from the client)",
  breakdown_fees: "Fees",
  breakdown_fee_coloca: "Coloca fee",
  breakdown_fee_network: "Network cost (included)",
  breakdown_tax_absorbed: "absorbed by Coloca",
  breakdown_economic_model: "Economic model",
  breakdown_net: "Net (the beneficiary receives)",

  // Timeline
  timeline_title: "Timeline · audit",
  timeline_source_staff: "Coloca team",
  timeline_source_network: "Banking network confirmation",
  timeline_source_system: "System",
  timeline_by: "by {{actor}}",
  timeline_error: "The timeline could not be loaded.",

  // Drawer actions
  actions_cancel: "Cancel and return funds",
  actions_retry: "Retry send",
  processing_note: "At the bank — confirmation arrives in minutes. No actions available.",
  reconcile_soon: "Reconcile (coming soon)",
  terminal_note: "Operation finished — read only.",

  // Retry modal
  retry_title: "Retry payout send",
  retry_warning: "This action sends real money and is irreversible. Review the details before confirming.",
  retry_summary_beneficiary: "Beneficiary",
  retry_summary_corridor: "Corridor",
  retry_summary_org: "Organization",
  retry_sends_label: "Sent to the beneficiary",
  retry_debits_caption: "{{gross}} is debited from the client (fees included)",
  retry_confirm_check: "I confirm the beneficiary, destination and amount are correct.",
  retry_confirm_check_no_destination: "I confirm the organization, corridor and amount are correct.",
  retry_cancel: "Cancel",
  retry_arming: "Review the summary…",
  retry_confirm: "Confirm send",
  retry_sending_title: "Sending to the bank…",
  retry_sending_caption: "Contacting the {{corridor}} network. Do not close this window.",
  retry_done_processing_title: "Sent to the bank",
  retry_done_processing_msg: "It is on its way. Bank confirmation arrives in minutes.",
  retry_done_paid_title: "Paid!",
  retry_done_paid_msg:
    "The bank settled the transfer immediately. The beneficiary already received the funds.",
  retry_done_failed_title: "Rejected by the bank",
  retry_done_failed_msg:
    "The network rejected the operation. The funds returned to the client's available balance. It cannot be re-sent.",
  retry_done_stuck_title: "Could not send",
  retry_done_stuck_msg:
    "The corridor is not configured. Contact platform and retry once it is fixed. The payout still needs rescuing.",
  retry_done_race_title: "Already processed",
  retry_done_race_msg: "Another operator processed this payout. It was updated with its current status.",
  retry_done_button: "Done",

  // Cancel modal
  cancel_title: "Cancel payout",
  cancel_body:
    "The reserved funds ({{gross}}) return to {{org}}'s available balance. The payout will be marked as Cancelled.",
  cancel_reason_label: "Reason (optional)",
  cancel_reason_placeholder: "E.g. invalid beneficiary details",
  cancel_back: "Back",
  cancel_confirm: "Cancel and return funds",
  cancel_error: "The payout could not be cancelled.",

  // Toasts
  toast_retry_processing: "Sent — waiting for bank confirmation",
  toast_retry_paid: "Payout paid",
  toast_retry_failed: "Rejected by the bank — funds returned",
  toast_retry_stuck: "Could not send — corridor not configured",
  toast_race: "Already processed by another operator",
  toast_cancelled: "Funds returned to the client",
};
